import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { InstallatoerDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/installatoer-dto';

@Component({
    selector: 'app-installatoer-input',
    templateUrl: './installatoer-input.component.html',
    styleUrls: ['./installatoer-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InstallatoerInputComponent implements OnInit, OnDestroy {
  installatoer: InstallatoerDto;

  private _installatoerer = new BehaviorSubject<InstallatoerDto[]>([]);
  @Input() set installatoerer(value: InstallatoerDto[]) {
    this._installatoerer.next(value);
  }

  @Input() form: UntypedFormGroup;

  @Output() installatoerChange: EventEmitter<string> = new EventEmitter();

  get installatoerer() {
    return this._installatoerer.getValue();
  }

  filteredInstallatoerer: Observable<string[]>;
  selectedInstallatoer: string[] = [];
  @Input() appearance: string = 'outline';
  @Input() formFieldName: string = 'installatoerId';
  @Input() formAlternativFieldName: string = 'alternativInstallatoer';
  @Input() showLabel: boolean = true;

  installatoerCtrl = new UntypedFormControl();
  @ViewChild('installatoerInput') installatoerInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { read: MatAutocompleteTrigger }) matAutocomplete: MatAutocomplete;
  autocompleteTrigger: MatAutocompleteTrigger;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  visible = true;
  removable = true;

  installatoerLabel = 'Indtast installatør';
  unsubscribe$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this._installatoerer.subscribe(() => {
      this.filteredInstallatoerer = this.installatoerCtrl.valueChanges.pipe(
        takeUntil(this.unsubscribe$),
        startWith(null),
        map((installatoer: string | null) => (installatoer ? this._filter(installatoer) : []))
      );
    });
    const installatoerId = this.form?.get(this.formFieldName)?.value;
    const alternativInstallatoer = this.form?.get(this.formAlternativFieldName)?.value;
    if (installatoerId) {
      var current = Number(installatoerId);
      this.installatoer = this.installatoerer.find((x) => x.id == current);
      if (this.installatoer) this.selectedInstallatoer.push(this.installatoer.name);
    } else if (alternativInstallatoer) this.selectedInstallatoer.push(alternativInstallatoer);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  addInstallatoer(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if (this.selectedInstallatoer.length === 0 && (value || '').trim()) {
      this.selectedInstallatoer.push(value.trim());
      this.installatoerChange.emit(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = null;
    }
  }

  removeInstallatoer(): void {
    this.installatoer = null;
    this.selectedInstallatoer = [];
    this.installatoerCtrl.enable();
    this.installatoerChange.emit('');
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.installatoer = this.installatoerer.find((x) => x.name === event.option.viewValue);
    this.selectedInstallatoer[0] = event.option.viewValue;
    this.installatoerInput.nativeElement.value = '';
    this.installatoerChange.emit(event.option.viewValue);
    this.installatoerCtrl.setValue(null);
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.installatoerer
      .filter(
        (installatoer) =>
          (installatoer.name.toLowerCase().indexOf(filterValue) === 0 || installatoer.cvr.indexOf(filterValue) === 0) &&
          installatoer !== this.selectedInstallatoer[0]
      )
      .map((x) => x.name);
  }
}
